<template>
  <div class="reprice-approval-page">

    <!-- ── Loading ─────────────────────────────────────────────────────────── -->
    <div v-if="state === 'loading'" class="state-card">
      <div class="spinner" />
      <p class="state-label">Loading reprice details…</p>
    </div>

    <!-- ── Not found ───────────────────────────────────────────────────────── -->
    <div v-else-if="state === 'not-found'" class="state-card state-error">
      <div class="state-icon">✕</div>
      <h2>Link Not Found</h2>
      <p>This approval link is invalid or has expired.</p>
    </div>

    <!-- ── Already processed ───────────────────────────────────────────────── -->
    <div v-else-if="state === 'already-processed'" class="state-card state-neutral">
      <div class="state-icon">✓</div>
      <h2>Already Processed</h2>
      <p>
        This reprice is already
        <strong>{{ (pendingData.data.status || '').replace('_', ' ') }}</strong>.
      </p>
      <p v-if="pendingData.data.approved_by_email" class="sub">
        Approved by {{ pendingData.data.approved_by_email }}
      </p>
    </div>

    <!-- ── Approval card ────────────────────────────────────────────────────── -->
    <div v-else-if="state === 'ready' || state === 'confirmed'" class="approval-wrapper">

      <!-- Page header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">Reprice Approval</h1>
          <p class="page-sub">Review the price change and approve if correct</p>
        </div>
        <button
          v-if="state === 'ready' && !isExpired"
          class="approve-btn"
          :disabled="approving"
          @click="handleApprove"
        >
          <span v-if="approving">Submitting…</span>
          <span v-else>✓ &nbsp;Approve This Reprice</span>
        </button>
      </div>

      <!-- Approve error -->
      <p v-if="approveError" class="approve-error" style="text-align:right;margin-bottom:12px;">
        {{ approveError }}
      </p>

      <!-- Deadline banner -->
      <div class="deadline-banner">
        <span class="deadline-icon">⏱</span>
        <div>
          <strong>Auto-approves:</strong> {{ deadlineDisplay }}
          <span v-if="isExpired" class="expired-pill">EXPIRED — auto-approved</span>
        </div>
      </div>

      <!-- Loan card -->
      <div class="loan-card">

        <div class="card-header-row">
          <div>
            <p class="field-label">Loan Number</p>
            <p class="loan-number">{{ loan.loan_number }}</p>
            <p class="field-label mt8">Borrower</p>
            <p class="borrower-name">{{ loan.borrower_first }} {{ loan.borrower_last }}</p>
          </div>
          <span class="badge-pending">PENDING APPROVAL</span>
        </div>

        <hr class="card-divider" />

        <!-- Net Price Change badge -->
        <div class="price-badge-wrap">
          <div :class="['price-badge', priceBadgeClass]">
            <div class="price-badge-label">Net Price Change</div>
            <div class="price-badge-value">{{ priceDiffDisplay }}</div>
          </div>
        </div>

        <!-- Lock Period + Product -->
        <div class="meta-grid">
          <div>
            <p class="field-label">Lock Period</p>
            <p class="meta-value">{{ loan.lock_period }} days</p>
          </div>
          <div>
            <p class="field-label">Product</p>
            <p class="meta-value">{{ loan.product_name || '—' }}</p>
          </div>
        </div>

        <!-- Pricing table -->
        <p class="field-label mt8">PRICING DETAILS</p>
        <table class="pricing-table">
          <thead>
            <tr>
              <th class="th-left">Field</th>
              <th class="th-right">Locked</th>
              <th class="th-right">Repriced</th>
              <th class="th-right">Difference</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Rate</td>
              <td class="td-right">{{ fmt(loan.locked_rate) }}</td>
              <td class="td-right">{{ fmt(loan.repriced_rate) }}</td>
              <td class="td-right muted">—</td>
            </tr>
            <tr class="highlight-row">
              <td><strong>Net Price</strong></td>
              <td class="td-right"><strong>{{ fmt(loan.locked_net_price) }}</strong></td>
              <td class="td-right"><strong>{{ fmt(loan.repriced_net_price) }}</strong></td>
              <td class="td-right" :class="diffColorClass">
                <strong>{{ priceDiffDisplay }}</strong>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- LLM summary -->
        <div v-if="loan.llm_summary" class="summary-box">
          <p class="field-label">Summary</p>
          <p class="summary-text">{{ loan.llm_summary }}</p>
        </div>

      </div>
      <!-- /loan-card -->

      <!-- Approve note -->
      <p v-if="state === 'ready' && !isExpired" class="approve-note">
        Approving!
        This action is logged and cannot be undone.
      </p>

      <!-- ── Expired — waiting for auto-commit ─────────────────────────────── -->
      <div v-if="state === 'ready' && isExpired" class="info-box">
        The approval deadline has passed. This reprice will be auto-approved and
        committed on the next processing cycle.
      </div>

      <!-- ── Success ───────────────────────────────────────────────────────── -->
      <div v-if="state === 'confirmed'" class="success-card">
        <div class="success-icon">✓</div>
        <h2>Approved!</h2>
        <p>The reprice has been approved and submitted to Polly.</p>
        <p class="sub">Logged as <strong>{{ userEmail }}</strong></p>
      </div>

    </div>
    <!-- /approval-wrapper -->

  </div>
</template>

<script>
import apiService from '@/api/api.service.js'
import auth from '@/authConfig.js'

export default {
  name: 'RepriceApproval',

  data() {
    return {
      token:        null,
      pendingId:    null,
      state:        'loading',
      pendingData:  null,
      approving:    false,
      approveError: null
    }
  },

  computed: {
    loan() {
      return this.pendingData?.data ?? {}
    },
    azureId() {
      return localStorage.getItem('azure_id') || ''
    },
    userEmail() {
      return localStorage.getItem('authenticated_email') ||
             localStorage.getItem('azure_email') || ''
    },
    priceDiff() {
      return this.loan.price_difference ?? 0
    },
    priceDiffDisplay() {
      const d = this.priceDiff
      if (d === 0) return '0'
      return d > 0 ? `+${Number(d).toFixed(4)}` : `${Number(d).toFixed(4)}`
    },
    priceBadgeClass() {
      const d = this.priceDiff
      if (d > 0) return 'price-badge-positive'
      if (d < 0) return 'price-badge-negative'
      return 'price-badge-neutral'
    },
    diffColorClass() {
      const d = this.priceDiff
      if (d > 0) return 'color-positive'
      if (d < 0) return 'color-negative'
      return ''
    },
    deadlineDisplay() {
      const dl = this.loan.approval_deadline_utc
      if (!dl) return '—'
      return new Date(dl).toLocaleString('en-US', {
        timeZone:     'America/New_York',
        weekday:      'long',
        month:        'long',
        day:          'numeric',
        hour:         'numeric',
        minute:       '2-digit',
        timeZoneName: 'short'
      })
    },
    isExpired() {
      const dl = this.loan.approval_deadline_utc
      if (!dl) return false
      return new Date() > new Date(dl)
    }
  },

  async created() {
    this.pendingId = this.$route.query.token

    if (!this.pendingId) {
      this.state = 'not-found'
      return
    }

    try {
      const tokenResponse = await auth.acquireTokenResponse()
      this.token = tokenResponse?.accessToken ?? null
    } catch {
      this.token = null
    }

    await this.loadPendingData()
  },

  methods: {
    fmt(val) {
      if (val == null) return '—'
      return Number(val).toFixed(4)
    },

    async loadPendingData() {
      try {
        const res = await apiService.GetRepricePendingApprovalById(
          this.pendingId,
          this.token
        )
        this.pendingData = res.data

        const status = this.pendingData?.data?.status ?? ''
        this.state = status === 'pending' ? 'ready' : 'already-processed'
      } catch (err) {
        this.state = 'not-found'
        console.error('[RepriceApproval] loadPendingData error', err)
      }
    },

    async handleApprove() {
      if (this.approving) return
      this.approving    = true
      this.approveError = null

      try {
        await apiService.ApproveRepricePending(
          this.pendingId,
          this.azureId,
          this.userEmail,
          this.token
        )
        this.state = 'confirmed'
      } catch (err) {
        this.approveError =
          err?.response?.data?.message ||
          'An error occurred while submitting the approval. Please try again.'
      } finally {
        this.approving = false
      }
    }
  }
}
</script>

<style scoped>
.reprice-approval-page {
  min-height: 100vh;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 16px 64px;
  font-family: 'Segoe UI', Arial, sans-serif;
  color: #0f172a;
  overflow: auto;
}

.state-card {
  background: #fff;
  border-radius: 12px;
  padding: 48px 40px;
  text-align: center;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 1px 4px rgba(0,0,0,.08);
  margin-top: 40px;
}
.state-card h2 { margin: 12px 0 8px; }
.state-card p  { color: #64748b; margin: 4px 0 0; font-size: 14px; }
.state-error   { border-top: 4px solid #dc2626; }
.state-neutral { border-top: 4px solid #0f172a; }
.state-icon    { font-size: 42px; margin-bottom: 8px; }
.sub { font-size: 12px !important; color: #94a3b8 !important; margin-top: 6px !important; }
.state-label { color: #64748b; margin-top: 8px; }

.spinner {
  width: 36px; height: 36px;
  border: 3px solid #e2e8f0;
  border-top-color: #0f172a;
  border-radius: 50%;
  animation: spin .75s linear infinite;
  margin: 0 auto 16px;
}
@keyframes spin { to { transform: rotate(360deg); } }

.approval-wrapper {
  max-width: 620px;
  width: 100%;
  margin-top: 8px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 20px;
}
.page-title { margin: 0; font-size: 22px; font-weight: 700; }
.page-sub   { margin: 2px 0 0; font-size: 13px; color: #64748b; }

.deadline-banner {
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13px;
  color: #78350f;
  margin-bottom: 20px;
}
.deadline-icon { font-size: 18px; flex-shrink: 0; margin-top: 1px; }
.expired-pill {
  display: inline-block;
  background: #dc2626;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  margin-left: 8px;
  vertical-align: middle;
}

.loan-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
}
.card-header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}
.field-label {
  font-size: 11px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 2px;
}
.mt8           { margin-top: 10px !important; }
.loan-number   { font-size: 22px; font-weight: 800; margin: 0; }
.borrower-name { font-size: 16px; font-weight: 700; margin: 0; }
.badge-pending {
  display: inline-block;
  background: #fef3c7;
  color: #92400e;
  font-size: 11px;
  font-weight: 700;
  padding: 5px 12px;
  border-radius: 999px;
  white-space: nowrap;
  flex-shrink: 0;
}
.card-divider { border: none; border-top: 1px solid #f1f5f9; margin: 14px 0; }

.price-badge-wrap  { text-align: center; margin-bottom: 16px; }
.price-badge {
  display: inline-block;
  padding: 14px 32px;
  border-radius: 12px;
  border-width: 3px;
  border-style: solid;
  min-width: 160px;
  text-align: center;
}
.price-badge-positive { background: #dcfce7; color: #166534; border-color: #166534; }
.price-badge-negative { background: #fee2e2; color: #991b1b; border-color: #991b1b; }
.price-badge-neutral  { background: #dcfce7; color: #166534; border-color: #166534; }
.price-badge-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 6px;
  opacity: .75;
}
.price-badge-value { font-size: 36px; font-weight: 900; line-height: 1; }

.meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 14px;
}
.meta-value { font-size: 13px; font-weight: 600; margin: 0; }

.pricing-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-bottom: 14px;
}
.pricing-table th {
  padding: 8px 10px;
  font-weight: 600;
  color: #475569;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}
.pricing-table td { padding: 8px 10px; color: #334155; border-bottom: 1px solid #f1f5f9; }
.th-left   { text-align: left; }
.th-right  { text-align: right; }
.td-right  { text-align: right; }
.muted     { color: #94a3b8; }
.highlight-row  { background: #fef9c3; }
.color-positive { color: #166534; }
.color-negative { color: #991b1b; }

.summary-box  { background: #f8fafc; border-radius: 6px; padding: 12px; }
.summary-text { font-size: 13px; color: #334155; margin: 4px 0 0; line-height: 1.6; }

.approve-btn {
  background: #0f172a;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  padding: 12px 28px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  letter-spacing: 0.2px;
  white-space: nowrap;
  transition: background .15s ease;
  flex-shrink: 0;
}
.approve-btn:hover:not(:disabled) { background: #1e293b; }
.approve-btn:disabled { opacity: .55; cursor: not-allowed; }
.approve-note  { font-size: 12px; color: #64748b; margin: 0 0 20px; text-align: center; }
.approve-error { color: #dc2626; font-size: 13px; font-weight: 600; }

.info-box {
  background: #f1f5f9;
  border-radius: 8px;
  padding: 14px 16px;
  font-size: 13px;
  color: #475569;
  text-align: center;
  margin-bottom: 24px;
}

.success-card {
  background: #dcfce7;
  border: 2px solid #166534;
  border-radius: 8px;
  padding: 28px;
  text-align: center;
  margin-bottom: 24px;
}
.success-icon { font-size: 40px; margin-bottom: 8px; }
.success-card h2 { color: #166534; margin: 0 0 8px; }
.success-card p  { color: #14532d; font-size: 13px; margin: 4px 0 0; }
</style>